import React from 'react';
import { format, parseISO } from 'date-fns';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import RightBar from '../components/RightBar';
import Calendar from '../components/calendar/Calendar';
import MainContent from '../components/MainContent';
import KpiCard from '../components/dashboard/KpiCard';
import AlertCard from '../components/dashboard/AlertCard';
import LiveCohortHealth from '../components/dashboard/LiveCohortHealth';
import OnboardingFunnel from '../components/dashboard/OnboardingFunnel';
import WeeklyActivity from '../components/dashboard/WeeklyActivity';
import {
  GenderDonut,
  TopCountries,
} from '../components/dashboard/Demographics';
import useFetchOverview from '../hooks/useFetchOverview';
import DownloadButton from '../components/analytics/DownloadButton';

const formatDate = (date) => format(parseISO(date), 'd MMM yyyy');

function ChangeBadge({ changePercent }) {
  if (!changePercent) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-xs px-2 py-0.5">
        No change vs previous 7 days
      </span>
    );
  }
  const isUp = changePercent > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-2 py-0.5 ${
        isUp ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
      }`}
    >
      {isUp ? (
        <TrendingUpIcon fontSize="inherit" />
      ) : (
        <TrendingDownIcon fontSize="inherit" />
      )}
      {isUp ? '+' : '−'}
      {Math.abs(changePercent)}%
      <span className="font-normal">vs previous 7 days</span>
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-5 animate-pulse" aria-label="Loading dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((key) => (
          <div
            key={key}
            className="h-32 bg-white rounded-xl border border-gray-200"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2].map((key) => (
          <div
            key={key}
            className="h-14 bg-white rounded-xl border border-gray-200"
          />
        ))}
      </div>
      <div className="h-72 bg-white rounded-xl border border-gray-200" />
      <div className="h-64 bg-white rounded-xl border border-gray-200" />
    </div>
  );
}

function ErrorState({ status, onRetry, isRetrying }) {
  if (status === 403) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-600">
        The dashboard overview is only available to administrators.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8 text-center space-y-4">
      <ErrorOutlineIcon className="text-gray-400" fontSize="large" />
      <p className="text-sm text-gray-600">
        {status === 502
          ? 'The analytics service is unreachable right now.'
          : "We couldn't load the dashboard data."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={isRetrying}
        className="bg-claret-500 hover:bg-claret-400 px-4 py-1.5 rounded-md text-white text-sm disabled:opacity-60"
      >
        {isRetrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}

function Dashboard() {
  const { data, isLoading, isError, errorStatus, refetch, isFetching } =
    useFetchOverview();

  const kpis = data?.kpis;
  const liveCohorts = data?.live_cohorts ?? [];
  const weeks = data?.trends?.weeks ?? [];
  const demographics = data?.demographics;
  const excludedTestCohorts = data?.notes?.excluded_test_cohorts ?? [];
  const nextCohort = kpis?.live_cohorts?.next_to_start;

  const renderBody = () => {
    if (isLoading) return <LoadingSkeleton />;

    if (isError && !data) {
      return (
        <ErrorState
          status={errorStatus}
          onRetry={refetch}
          isRetrying={isFetching}
        />
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Active learners (7d)"
            value={kpis?.active_learners_7d?.value}
            subtitle={`${
              kpis?.active_learners_7d?.percent_of_members ?? 0
            }% of ${(
              kpis?.active_learners_7d?.of_members ?? 0
            ).toLocaleString()} live members`}
            icon={<GroupsIcon fontSize="inherit" />}
            iconColour="#8B173B"
          >
            <div className="pt-1">
              <ChangeBadge
                changePercent={kpis?.active_learners_7d?.change_percent}
              />
            </div>
          </KpiCard>
          <KpiCard
            title="Live cohorts"
            value={kpis?.live_cohorts?.value}
            subtitle={
              nextCohort
                ? `Next: ${nextCohort.name}, ${formatDate(
                    nextCohort.start_date
                  )}`
                : 'No upcoming cohort scheduled'
            }
            icon={<WorkspacePremiumIcon fontSize="inherit" />}
            iconColour="#00999E"
          />
          <KpiCard
            title="Awaiting grading"
            value={kpis?.submissions_awaiting_grading}
            subtitle="Submissions across all cohorts"
            icon={<AssignmentOutlinedIcon fontSize="inherit" />}
            iconColour="#57C13E"
          />
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
            Needs attention
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AlertCard
              title="AI grading failed"
              value={kpis?.ai_grading_failed ?? 0}
              action="Review and retry in assignment analytics"
              to="/analytics/cohort-assignment-analytics"
              icon={<ErrorOutlineIcon />}
            />
            <AlertCard
              title="Emails failed (7 days)"
              value={kpis?.emails_failed_7d ?? 0}
              action="Check delivery on the email page"
              to="/emails"
              icon={<MailOutlineIcon />}
            />
          </div>
        </div>

        <LiveCohortHealth cohorts={liveCohorts} />
        <OnboardingFunnel cohorts={liveCohorts} />
        {weeks.length > 0 && <WeeklyActivity weeks={weeks} />}

        {demographics && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <GenderDonut gender={demographics.gender ?? []} />
            <TopCountries
              topCountries={demographics.top_countries ?? []}
              otherCountries={demographics.other_countries}
              countryUnknown={demographics.country_unknown}
            />
          </div>
        )}

        {excludedTestCohorts.length > 0 && (
          <p className="text-xs text-gray-400 pb-6">
            Test cohorts excluded: {excludedTestCohorts.join(', ')}.
          </p>
        )}
      </>
    );
  };

  return (
    <>
      <MainContent>
        <div className="space-y-5 pb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
              <p className="text-xs text-gray-500">
                Live cohorts, learner activity and items that need attention
              </p>
            </div>
            {data && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <DownloadButton
                  label="Live cohorts CSV"
                  path="/overview/export"
                  fallbackName="overview-live-cohorts.csv"
                />
                <DownloadButton
                  label="Weekly trends CSV"
                  path="/overview/export?view=trends"
                  fallbackName="overview-trends.csv"
                />
                {data.generated_at && (
                  <span>
                    Updated{' '}
                    {format(parseISO(data.generated_at), 'HH:mm, d MMM')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  title="Refresh"
                  aria-label="Refresh dashboard data"
                  className="h-8 w-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
                >
                  <RefreshIcon
                    fontSize="small"
                    className={isFetching ? 'animate-spin' : ''}
                  />
                </button>
              </div>
            )}
          </div>
          {renderBody()}
        </div>
      </MainContent>
      <RightBar>
        <Calendar />
      </RightBar>
    </>
  );
}

export default Dashboard;
